import { User } from 'next-firebase-auth';
import React, { useEffect, useState } from 'react';
import { ParsedLesson } from '@customtypes/parsedLesson';
import BugIconComponent from '../icons/BugIcon';
import ProgressNotSavedModalComponent from '@components/ProgressNotSavedModal';
import { useRouter } from 'next/router';
import { Button1Text, Heading2, Paragraph2 } from './texts/Texts';
import { getLinkToSection } from '@utils/link-utils-web';
import CloseIconComponent from '@components/icons/closeIcon';
import ChapterProgressBarComponent from './ChapterProgressBar';
import * as Sentry from '@sentry/nextjs';
import {
  clearAllLessonProgressFromIDB,
  getSavedLessonProgressFromIDB,
} from '@utils/idb/idb';
import UserReportComponent from '@components/userReport/userReport';
import {
  clearSavedFiles,
  clearSavedFilesFromPreview,
} from '@requests/learnRequests';
import RestartIconComponent from '@components/icons/restartIcon';
import { useQuery } from '@tanstack/react-query';
import { ModalComponent } from '@components/modal/Modal';
import toast from 'react-hot-toast';
import { ErrorToastComponent } from './ErrorToast';
import { SpacerComponent } from '@components/Spacer';
import PrimaryButtonComponent from '@components/buttons/PrimaryButton';
import LoadingIconComponent from '@components/icons/loadingIcon';
import { TutorialType } from '@customtypes/tutorial';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { DefaultTooltip } from '@utils/tooltip-utils';
import CloudStrikeThroughIconComponent from '@components/icons/cloudStrikeThrough';
import CloudWithCheckmarkIconComponent from '@components/icons/cloudWithCheckmark';
import useSubscriptionStatus from '@hooks/useSubscriptionStatus';
import Link from 'next/link';
import useIsEligibleForFreeMaxTrial from '@hooks/useIsEligibleForFreeMaxTrial';
import { TOOLTIP_DELAY_SHOW } from '@utils/constants';
import { useSpecialOfferWindow } from '@hooks/useSpecialOfferWindow';
import PulsingCircleIconComponent from '@components/icons/pulsingCircleIcon';
import { regular_xs } from './texts/Styles';
import { isUserCourseEngineer } from '@utils/content-utils';

const LESSON_NAVBAR_TOOLTIP_ID = 'lesson-navbar-tooltip';

type BackendConnectionState =
  | 'starting backend'
  | 'started backend'
  | 'failed backend';

type NavbarType =
  | {
      type: 'guidedProject';
      backendConnectionState?: BackendConnectionState;
    }
  | { type: 'course' }
  | { type: 'practiceRequired' }
  | { type: 'practiceOptional' }
  | { type: 'challenges' }
  | { type: 'quiz' }
  | { type: 'guidedProjectOptional' };

export function LessonNavbarWebComponent({
  selectedLessonIndex,
  lessons,
  chapterId,
  tutorialId,
  tutorialVersion,
  trackId,
  trackVersion,
  sectionIndex,
  user,
  tutorialType,
  isProgressBarHidden,
}: {
  selectedLessonIndex: number;
  lessons: ParsedLesson[];
  chapterId: number;
  tutorialId: number;
  tutorialVersion: number;
  trackId: number;
  trackVersion: number;
  sectionIndex: number;
  user: User;
  tutorialType: NavbarType;
  isProgressBarHidden?: boolean;
}) {
  const [shouldShowCourseBuilderLink, setShouldShowCourseBuilderLink] =
    useState(false);
  const [hasAnyProgress, setHasAnyProgress] = useState(false);
  const [showProgressNotSavedWarning, setShowProgressNotSavedWarning] =
    useState(false);

  const [showClearPreviewFilesModal, setShowClearPreviewFilesModal] =
    useState(false);
  const backlinkUrl = getLinkToSection(trackId, sectionIndex);
  const router = useRouter();
  const [subscriptionStatus] = useSubscriptionStatus(user);
  const [isEligibleForFreeTrial] = useIsEligibleForFreeMaxTrial(user);
  const specialOfferWindow = useSpecialOfferWindow(user);

  const courseBuilderLink = `https://teach.mimo.org/#/courses/${tutorialId}/chapters/${chapterId}/lessons/${lessons[selectedLessonIndex]?.id}`;

  const { isFetching, isError } = useQuery({
    queryKey: [`runfiles-${lessons[selectedLessonIndex]?.id}`],
    queryFn: async () => {},
    enabled: false,
  });

  useEffect(() => {
    setShouldShowCourseBuilderLink(isUserCourseEngineer(user));
  }, []);

  function handleProgressNotSaved() {
    // if the user has no progress, we don't want to show the modal
    if (!hasAnyProgress) {
      router.push(`${backlinkUrl}`);
    } else {
      setShowProgressNotSavedWarning(true);
    }
  }

  async function onModalQuit() {
    await Promise.all([
      clearAllLessonProgressFromIDB(),
      tutorialType.type === 'guidedProject' ||
      tutorialType.type === 'guidedProjectOptional'
        ? clearSavedFiles(trackId, tutorialId, chapterId, trackVersion, user)
        : Promise.resolve(),
    ]);
    router.push(`${backlinkUrl}`);
  }

  async function getLessonProgress() {
    try {
      const savedLessonProgress = await getSavedLessonProgressFromIDB(
        Number(tutorialId),
      );
      setHasAnyProgress(
        savedLessonProgress.some(
          (item: { chapterId: number }) => item.chapterId === chapterId,
        ),
      );
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          context: 'IDBException - getLessonProgress - LessonNavbar.tsx',
        },
      });
    }
  }

  useEffect(() => {
    getLessonProgress();
  }, [router.query.lesson]);

  function handleHideModal() {
    toast.dismiss();
    setShowClearPreviewFilesModal(false);
  }

  return (
    <>
      <ReactTooltip
        className="defaultTooltip"
        place="bottom"
        opacity={1}
        arrowColor="transparent"
        id={LESSON_NAVBAR_TOOLTIP_ID}
        delayShow={TOOLTIP_DELAY_SHOW}
      ></ReactTooltip>
      {showClearPreviewFilesModal && (
        <ModalComponent handleHideModal={handleHideModal}>
          <ClearPreviewFilesModalContent
            handleHideModal={handleHideModal}
            trackId={trackId}
            tutorialId={tutorialId}
            chapterId={chapterId}
            user={user}
          ></ClearPreviewFilesModalContent>
        </ModalComponent>
      )}
      {showProgressNotSavedWarning && (
        <ProgressNotSavedModalComponent
          handleDismiss={() => setShowProgressNotSavedWarning(false)}
          handleQuit={() => onModalQuit()}
        ></ProgressNotSavedModalComponent>
      )}
      <div className="z-[31] flex h-12 flex-shrink-0 items-center space-x-4 bg-product2-background-secondary pr-2 text-product-text-secondary-dark">
        <div className="flex h-full flex-1 items-center pl-2">
          <button
            className="mr-4 flex h-8 w-8 items-center justify-center rounded-lg text-product-button-text-enabled-dark hover:bg-product2-background-tertiary"
            onClick={handleProgressNotSaved}
            data-tooltip-id={LESSON_NAVBAR_TOOLTIP_ID}
            data-tooltip-html={DefaultTooltip('Close')}
          >
            <CloseIconComponent className="h-6" />
          </button>
          {((tutorialType.type === 'guidedProject' &&
            !tutorialType.backendConnectionState) ||
            tutorialType.type === 'challenges') &&
            getIconForProjectRunStatus(isFetching, isError)}
          {tutorialType.type === 'guidedProject' &&
            tutorialType.backendConnectionState && (
              <div className="flex cursor-default items-center rounded-xl px-2 py-1 hover:bg-product2-background-tertiary">
                <BackendConnectionStatusIconComponent
                  backendConnectionState={tutorialType.backendConnectionState}
                />
              </div>
            )}
        </div>
        {isProgressBarHidden || lessons.length === 1 ? (
          <></>
        ) : (
          <ChapterProgressBarComponent
            lessons={lessons}
            tutorialId={tutorialId}
          />
        )}
        <div className="flex flex-1 items-center justify-end space-x-2 whitespace-nowrap text-xs">
          {shouldShowCourseBuilderLink && (
            <>
              <ReactTooltip
                className="defaultTooltip"
                place="bottom"
                arrowColor="transparent"
                id="show-lesson-in-cb-tooltip"
                opacity={1}
                delayShow={TOOLTIP_DELAY_SHOW}
              ></ReactTooltip>
              <a
                href={courseBuilderLink}
                target="_blank"
                rel="noreferrer"
                className="flex h-8 w-8 items-center justify-center rounded-lg text-xs font-bold text-product2-content-primary hover:bg-product2-background-tertiary"
                data-tooltip-id="show-lesson-in-cb-tooltip"
                data-tooltip-html={DefaultTooltip(
                  'Show lesson in course builder',
                )}
              >
                <BugIconComponent className="h-5 w-5"></BugIconComponent>
              </a>
            </>
          )}
          {router.asPath.includes('/ssr/') && (
            <>
              <ReactTooltip
                className="defaultTooltip"
                place="bottom"
                arrowColor="transparent"
                id="reset-preview-files-tooltip"
                opacity={1}
                delayShow={TOOLTIP_DELAY_SHOW}
              ></ReactTooltip>
              <button
                className="flex h-8 w-8 items-center justify-center rounded-lg text-xs font-bold text-product2-content-primary hover:bg-product2-background-tertiary"
                onClick={() => setShowClearPreviewFilesModal(true)}
                data-tooltip-id="reset-preview-files-tooltip"
                data-tooltip-html={DefaultTooltip('Clear saved preview files')}
              >
                <RestartIconComponent className="h-5 w-5"></RestartIconComponent>
              </button>
            </>
          )}
          <UserReportComponent
            lessonId={lessons[selectedLessonIndex]?.id}
            tutorialId={tutorialId}
            tutorialVersion={tutorialVersion}
          />
          {isEligibleForFreeTrial ? (
            <Link
              href={{
                pathname: '/web/subscribe',
                query: { ...router.query, trackingReferer: 'web_navbar' },
              }}
            >
              <button className="h-8 cursor-pointer rounded-lg from-[#7E4BDE] to-[#9C75E6] px-4 font-mimopro text-sm font-bold leading-5 text-product-button-primary-text-light hover:bg-product2-background-accent   [&:not(:hover)]:bg-gradient-to-br">
                {specialOfferWindow.type === 'Loading' ? (
                  <LoadingIconComponent className="h-4 w-4 animate-spin" />
                ) : specialOfferWindow.type !== 'None' ? (
                  '7 days free + ' +
                  specialOfferWindow.discountPercent +
                  '% off'
                ) : (
                  'Start free trial'
                )}
              </button>
            </Link>
          ) : subscriptionStatus === 'not active' ||
            subscriptionStatus === 'active PRO' ? (
            <Link
              href={{
                pathname: '/web/subscribe',
                query: { ...router.query, trackingReferer: 'web_navbar' },
              }}
            >
              <button className="h-8 cursor-pointer rounded-lg from-[#7E4BDE] to-[#9C75E6] px-4 font-mimopro text-sm font-bold leading-5 text-product-button-primary-text-light  hover:bg-product2-background-accent [&:not(:hover)]:bg-gradient-to-br">
                {specialOfferWindow.type === 'Loading' ? (
                  <LoadingIconComponent className="h-4 w-4 animate-spin" />
                ) : specialOfferWindow.type !== 'None' ? (
                  'Upgrade + ' + specialOfferWindow.discountPercent + '% off'
                ) : (
                  'Upgrade now'
                )}
              </button>
            </Link>
          ) : null}
        </div>
      </div>
    </>
  );
}

function getIconForProjectRunStatus(isFetching: boolean, isError: boolean) {
  if (isFetching) {
    return (
      <div
        className="flex h-6 w-6 items-center justify-center rounded-full hover:bg-product2-background-tertiary"
        data-tooltip-id={LESSON_NAVBAR_TOOLTIP_ID}
        data-tooltip-html={DefaultTooltip('Your code is running')}
      >
        <LoadingIconComponent className="h-4 w-4 animate-spin text-product2-content-primary"></LoadingIconComponent>
      </div>
    );
  } else if (isError) {
    return (
      <div
        className="flex h-6 w-6 items-center justify-center rounded-full bg-product2-background-error"
        data-tooltip-id={LESSON_NAVBAR_TOOLTIP_ID}
        data-tooltip-html={DefaultTooltip(
          'There was an issue running/saving your code.',
        )}
      >
        <CloudStrikeThroughIconComponent className="h-4 w-4 text-product2-content-primary"></CloudStrikeThroughIconComponent>
      </div>
    );
  } else {
    return (
      <div
        className="flex h-6 w-6 items-center justify-center rounded-full hover:bg-product2-background-tertiary"
        data-tooltip-id={LESSON_NAVBAR_TOOLTIP_ID}
        data-tooltip-html={DefaultTooltip('Your code is synced')}
      >
        <CloudWithCheckmarkIconComponent className="h-4 w-4 text-product2-content-primary"></CloudWithCheckmarkIconComponent>
      </div>
    );
  }
}

function BackendConnectionStatusIconComponent({
  backendConnectionState,
}: {
  backendConnectionState: BackendConnectionState;
}) {
  switch (backendConnectionState) {
    case 'starting backend':
      return (
        <div className="flex items-center space-x-2">
          <PulsingCircleIconComponent
            className="h-2 w-2"
            circleClassName="bg-yellow-400"
          />
          <span className={`text-product2-content-primary ${regular_xs}`}>
            Starting the backend
          </span>
        </div>
      );
    case 'started backend':
      return (
        <div className="flex items-center space-x-2">
          <PulsingCircleIconComponent
            className="h-2 w-2"
            circleClassName="bg-product-accent-success-light"
          />
          <span className={`text-product2-content-primary ${regular_xs}`}>
            Backend is running
          </span>
        </div>
      );
    case 'failed backend':
      return (
        <div className="flex items-center space-x-2">
          <PulsingCircleIconComponent
            className="h-2 w-2"
            circleClassName="bg-red-400"
          />
          <span className={`text-product2-content-primary ${regular_xs}`}>
            Backend is not running
          </span>
        </div>
      );
      break;
  }
}

function ClearPreviewFilesModalContent({
  trackId,
  tutorialId,
  chapterId,
  user,
  handleHideModal,
}: {
  trackId: number;
  tutorialId: number;
  chapterId: number;
  user: User;
  handleHideModal: () => void;
}) {
  const router = useRouter();
  const {
    refetch: clearPreviewFiles,
    isFetching: clearPreviewFilesInProgress,
  } = useQuery({
    queryKey: ['clear-preview-files'],
    queryFn: () =>
      clearSavedFilesFromPreview(trackId, tutorialId, chapterId, user),
    enabled: false,
    retry: false,
  });

  async function handleClearPreviewFiles() {
    const clearResult = await clearPreviewFiles();
    if (clearResult.isSuccess) {
      toast.dismiss();
      handleHideModal();
      router.reload();
    } else {
      toast.dismiss();
      toast.custom((toast) => (
        <ErrorToastComponent toast={toast}>
          Unable to clear preview files. Please try again.
        </ErrorToastComponent>
      ));
    }
  }

  return (
    <div
      onClick={handleHideModal}
      aria-hidden="true"
      className="fixed bottom-0 left-0 top-0 z-[100] flex w-[100%] items-center justify-center bg-product-background-overlay-dark"
    >
      <div
        aria-hidden="true"
        className="relative flex w-[95%] max-w-[500px] flex-col items-center overflow-y-auto rounded-md bg-product-background-secondary-dark px-6 pb-8 pt-6"
        onClick={(e) => e.stopPropagation()}
      >
        <Heading2 className="text-center text-2xl font-bold text-product-text-primary-dark">
          Are you sure you want to clear the progress in this chapter?
        </Heading2>
        <SpacerComponent className="h-2"></SpacerComponent>
        <Paragraph2 className="text-center text-product-text-secondary-dark">
          All progress you made in this chapter will be lost
        </Paragraph2>
        <SpacerComponent className="h-8"></SpacerComponent>
        <PrimaryButtonComponent
          className="w-full"
          onClick={
            clearPreviewFilesInProgress ? undefined : handleClearPreviewFiles
          }
        >
          {clearPreviewFilesInProgress ? (
            <LoadingIconComponent className="h-6 w-6 animate-spin"></LoadingIconComponent>
          ) : (
            <Button1Text>Clear Progress</Button1Text>
          )}
        </PrimaryButtonComponent>
        <SpacerComponent className="h-4"></SpacerComponent>
        <button
          onClick={handleHideModal}
          className={`flex h-10 w-full items-center justify-center rounded-xl border-2 border-product-button-secondary-enabled-dark bg-product-background-secondary-dark bg-opacity-20 px-4 text-base font-semibold text-product-button-secondary-enabled-dark hover:bg-product-button-secondary-hovered-dark focus:outline-none`}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
